<script setup lang="ts">
import type { ISkuGroup, TSkuSelectorProduct } from '../models';
import type { IProductEntityCharacteristic, TProductCharacteristicType } from '~/entities';

import { CharacteristicsKeyGenerator } from '../business/CharacteristicsKeyGenerator';
import { CharacteristicsController } from '../business/CharacteristicsController';
import { EProductCharacteristicType } from '~/entities';

import { MMText } from '@mm-frontend/mm-web-ui-kit';
import SkuOptionText from './SkuOptionText.vue';
import SkuOptionImage from './SkuOptionImage.vue';
import SkuOptionColor from './SkuOptionColor.vue';

const props = defineProps<{
  products: TSkuSelectorProduct[];
  characteristics: IProductEntityCharacteristic[];
}>();

const selectedProduct = defineModel<TSkuSelectorProduct>();
const currentSelectedKey = ref();

const characteristicsController = new CharacteristicsController(
  props.products,
  props.characteristics
);

function onSelectOption(skuGroup: ISkuGroup, selectedId: number) {
  skuGroup.selectedOptionId.value = selectedId;
  currentSelectedKey.value = characteristicsController.getSelectedKey();
  selectedProduct.value = characteristicsController.getSelectedProduct();
}

watch(selectedProduct, syncLocalStates, { immediate: true });

function syncLocalStates() {
  if (!selectedProduct.value) return;

  const newKey = CharacteristicsKeyGenerator.byProduct(
    selectedProduct.value,
    characteristicsController.skuGroupsIds
  );
  /** не реагируем на локальные изменения */
  if (newKey === currentSelectedKey.value) return;

  characteristicsController.setSelectionByProduct(selectedProduct.value);
}

const optionComponentsMap = new Map<TProductCharacteristicType, Component>([
  [EProductCharacteristicType.IMAGE, SkuOptionImage],
  [EProductCharacteristicType.COLOR, SkuOptionColor],
  [EProductCharacteristicType.TEXT, SkuOptionText],
]);
</script>

<template>
  <div class="sku-selectors">
    <div
      v-for="skuGroup in characteristicsController.skuGroups"
      :key="skuGroup.id"
      class="sku-selectors__group"
    >
      <div class="sku-selectors__group__label">
        <MMText font="Body M/Regular" color="tertiary">{{ skuGroup.title }}</MMText>
        <MMText font="Body M/Semibold">
          {{ characteristicsController.optionsTitles.get(skuGroup.selectedOptionId.value) }}
        </MMText>
      </div>

      <div class="sku-selectors__group__options">
        <component
          :is="optionComponentsMap.get(skuGroup.type)"
          v-for="option in skuGroup.options"
          :key="option.id"
          :option="option"
          :is-active="option.id === skuGroup.selectedOptionId.value"
          :is-unavailable="characteristicsController.isUnavailableOption(option)"
          @click="onSelectOption(skuGroup, option.id)"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="stylus">
.sku-selectors
  display flex
  flex-direction column
  gap 24px

  &__group
    display flex
    flex-direction column
    gap 12px
    &__label
      display flex
      gap 6px
    &__options
      display flex
      flex-wrap wrap
      gap 12px
</style>
